/**
 * Media Scroll — Frontend
 *
 * On scroll, activates the Media block whose linked Section has its vertical
 * centre at or above the active Media block's centre in the viewport. The swap
 * fires when at least 50 % of a section has entered the view, and reverses
 * correctly when scrolling back up.
 *
 * The trigger Y is measured on every update rather than cached at load time.
 * Caching at load causes a bad value when the container is below the fold
 * (getBoundingClientRect returns top > window.innerHeight), which makes every
 * section qualify and permanently activates the last one.
 *
 * Bypasses the WP Interactivity API reactive state system entirely to avoid
 * server-side double-processing of directives on nested interactive blocks.
 *
 * Data attributes (set by render.php):
 *   Container: data-initial-section-id — first section's ID for initial state
 *   Section:   data-section-id         — this section's stable UUID
 *   Media:     data-section-id         — the linked section's UUID
 */

function initMediaScroll( container ) {
	const sections = Array.from(
		container.querySelectorAll( '.wp-block-amp-child-media-scroll-section[data-section-id]' )
	);
	const mediaBlocks = Array.from(
		container.querySelectorAll( '.wp-block-amp-child-media-scroll-media[data-section-id]' )
	);

	if ( ! sections.length || ! mediaBlocks.length ) return;

	function setActive( sectionId ) {
		if ( ! sectionId ) return;
		mediaBlocks.forEach( ( block ) => {
			block.classList.toggle( 'is-active', block.dataset.sectionId === sectionId );
		} );
	}

	// Apply initial active state from the PHP-seeded attribute.
	const initialId =
		container.dataset.initialSectionId ||
		sections[ 0 ]?.dataset.sectionId ||
		'';

	if ( initialId ) setActive( initialId );

	/**
	 * Return the trigger Y: the vertical centre of the active media block.
	 *
	 * Only accepts a measurement when the block (or its parent) is actually
	 * within the viewport. Falls back to viewport centre if it is off-screen,
	 * which avoids the "huge number" bug when the container starts below fold.
	 */
	function getTriggerY() {
		const active =
			mediaBlocks.find( ( b ) => b.classList.contains( 'is-active' ) ) ||
			mediaBlocks[ 0 ];

		// Try the media block itself, then its parent (e.g. the sticky column).
		for ( const el of [ active, active.parentElement ] ) {
			if ( ! el ) continue;
			const r = el.getBoundingClientRect();
			if ( r.height > 0 && r.top < window.innerHeight && r.bottom > 0 ) {
				return r.top + r.height / 2;
			}
		}

		return window.innerHeight / 2;
	}

	function update() {
		const ty = getTriggerY();
		let current = null;

		for ( const section of sections ) {
			const r      = section.getBoundingClientRect();
			const centre = r.top + r.height * 0.15; // 0.35 = flip when 35 % of section has crossed the trigger line
			if ( centre <= ty ) {
				current = section;
			}
		}

		setActive( ( current || sections[ 0 ] ).dataset.sectionId );
	}

	window.addEventListener( 'scroll', update, { passive: true } );
	window.addEventListener( 'resize', update );

	// Run once so the scroll position is correct even on initial render.
	update();
}

// ES modules are deferred — DOM is ready when this runs.
document
	.querySelectorAll( '[data-wp-interactive="amp-child/mediaScroll"]' )
	.forEach( initMediaScroll );
